import { Controller } from "@hotwired/stimulus";
import Papa from "papaparse";
import xlsx from "xlsx";
import type ProgressController from "./progress_controller";
import type VaultController from "./vault_controller";

declare global {
	interface Window {
		vault: VaultController;
	}
}

interface CSV {
	data: string[];
	errors: string[];
	meta: {
		delimiter: string;
		linebreak: string;
		aborted: boolean;
		cursor: number;
		renamedHeaders: string[];
		truncated: boolean;
	};
}

class CountStream {
	readable;
	writable;
	constructor(context: any, contentLength: string | null) {
		const { readable, writable } = new TransformStream();
		const gen = readable.getReader();
		this.readable = new ReadableStream({
			start(controller) {
				this.currentContentLength = 0;
				this.contentLength = Number(contentLength);
				this.context = context;
			},
			async pull(controller) {
				const { done, value }: { done: boolean; value: Uint8Array } =
					await gen.read();
				if (done) return controller.close();

				this.currentContentLength += value.length;

				if (this.context.hasProgressOutlet) {
					this.context.progressOutlet?.updateText(
						`Receiving Data ${this.context.formatBytes(this.currentContentLength)}/${this.context.formatBytes(this.contentLength)}`,
					);
				}

				controller.enqueue(value);
			},
		});
		this.writable = writable;
	}
}

export default class extends Controller {
	static values = { url: String, filename: String, lines: Number, payloadCallback: String };
	static targets = ["button"];
	static outlets = ["progress"];

	started: boolean = false;
	urlValue!: string;
	linesValue!: number;
	buttonTarget!: HTMLButtonElement;
	filenameValue!: string;
	progressOutlet!: ProgressController;
	hasProgressOutlet!: boolean;
	payload: FormData | null = null;


	initialize() {}

	async downloadDecryptedXlsx() {
		await this.download(true, "xlsx");
	}

	async downloadDecryptedCsv() {
		await this.download(true, "csv");
	}

	async downloadEncryptedXlsx() {
		await this.download(false, "xlsx");
	}

	async downloadEncryptedCsv() {
		await this.download(false, "csv");
	}

	formatBytes(bytes: number, decimals = 2) {
		if (!+bytes) return "0 Bytes";

		const k = 1024;
		const dm = decimals < 0 ? 0 : decimals;
		const sizes = [
			"Bytes",
			"KiB",
			"MiB",
			"GiB",
			"TiB",
			"PiB",
			"EiB",
			"ZiB",
			"YiB",
		];

		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
	}

	headers: string[] = [];
	encryption_indices: number[] = [];
	current_line: number = 1;
	encryptedBlobRegex = /encrypted_blob\/(\d+)/;

	// Build a preview URL for encrypted blobs so the UI can show a decrypted image when unlocked.
	previewUrlForEncryptedBlob(value: string): string | null {
		const match = value.match(this.encryptedBlobRegex);
		if (!match) return null;
		return `${window.location.origin}/encrypted_blob/${match[1]}?preview=true`;
	}

	// we decrypt one row after the next and the first call should be the header
	// the Promise.all call should resolve each promise after the other
	// and the header should therefore be the first one
	// we are in the main Javascript thread and nothing should resolve in parallel
	async decryptRow(fields: string[]): Promise<string[]> {
		if (this.headers.length === 0) {
			this.headers = fields;

			for (const [index, header] of this.headers.entries()) {
				if (header.endsWith("_e")) {
					this.encryption_indices.push(index);
					this.headers[index] = this.headers[index]?.slice(0, -2);
				}
			}

			return this.headers;
		}

		for (const index of this.encryption_indices) {
			const value = fields[index];
			// Encrypted blob links are not decryptable strings; when the vault is open we swap them
			// for preview URLs and skip decryption, otherwise we decrypt normal encrypted fields.
			if (typeof value === "string" && value.match(this.encryptedBlobRegex)) {
				if (window.vault?.is_open?.()) {
					const previewUrl = this.previewUrlForEncryptedBlob(value);
					if (previewUrl) {
						fields[index] = previewUrl;
					}
				}
				continue;
			}
			fields[index] = await window.vault.decryptMessage(value);
		}

		if (this.hasProgressOutlet && this.current_line % 100 === 0) {
			this.progressOutlet?.updateText(
				`Decrypting Data ${this.current_line}/${this.linesValue}`,
			);
		}

		this.current_line += 1;

		return fields;
	}

	async leaveRow(fields: string[]): Promise<string[]> {
		return Promise.resolve(fields);
	}

	// this stream transformer will parse the already received lines
	// of the csv and apply the provided editRow function
	csvTransformer(
		editRow: (row: string[]) => Promise<string[]>,
	): TransformStream {
		const parse_chunk = async (
			chunk: string,
			editRow: (fields: string[]) => Promise<string[]>,
		): Promise<string[][]> => {
			const csv: Papa.ParseResult<string[]> = Papa.parse(chunk);

			const decrypted_csv = await Promise.all(
				csv.data.map((value: string[]): Promise<string[]> => {
					return editRow(value);
				}),
			);
			return decrypted_csv;
		};

		return new TransformStream({
			start() {
				this._leftover = "";
			},

			async transform(chunk, controller) {
				const text = this._leftover + new TextDecoder().decode(chunk);
				const last_newline = text.lastIndexOf("\n");

				// if we have no newline, it's a super small chunk
				if (last_newline === -1) {
					this._leftover = text;
				} else {
					const rows = text.slice(0, last_newline);
					this._leftover = text.slice(last_newline);

					controller.enqueue(
						new TextEncoder().encode(
							Papa.unparse(await parse_chunk(rows, editRow)),
						),
					);
				}
			},

			async flush(controller) {
				if (this._leftover) {
					controller.enqueue(
						new TextEncoder().encode(
							Papa.unparse(await parse_chunk(this._leftover, editRow)),
						),
					);
				}
			},
		});
	}

	async downloadFileEditRows(
		url: string,
		fileName: string,
		editRow: (row: string[]) => Promise<string[]>,
	) {
		// we can't laverage any browser features in the xlsx path
		// so it's the always the same, we could maybe build the excel
		// file while parsing the stream, but in the end the whole
		// file would sit in the ram anyways...
		try {
			if (fileName.endsWith("xlsx")) {
				const request = await this.fetchRequest(url);
				const length = request.headers.get("content-length");
				const request_stream = request.body
					?.pipeThrough(new CountStream(this, length))
					.pipeThrough(this.csvTransformer(editRow));

				const csv = Papa.parse(await new Response(request_stream).text());
				const wb = xlsx.utils.book_new();
				const ws = xlsx.utils.aoa_to_sheet(csv.data, {dense: true});

				xlsx.utils.book_append_sheet(wb, ws, "Export");

				xlsx.writeFileXLSX(wb, fileName, {
					compression: true,
					type: "file",
				});

				this.closeModal();
				return;
			}

			if (window.showSaveFilePicker) {
				// chrome based browser path
				const newFile = await window.showSaveFilePicker({
					suggestedName: fileName,
				});
				const writableStream = await newFile.createWritable();
				const request = await this.fetchRequest(url);
				const length = request.headers.get("content-length");

				request?.body
					?.pipeThrough(new CountStream(this, length))
					.pipeThrough(this.csvTransformer(editRow))
					.pipeTo(writableStream);
			} else {
				let request;
				request = await this.fetchRequest(url);

				const length = request.headers.get("content-length");
				const request_stream = request.body
					?.pipeThrough(new CountStream(this, length))
					.pipeThrough(this.csvTransformer(editRow));

				const blob = await new Response(request_stream).blob();
				const a = document.createElement("a");

				a.href = URL.createObjectURL(blob);
				a.download = fileName;
				a.click();
				URL.revokeObjectURL(a.href);
			}

			this.closeModal();
			console.log("FETCH", request.status, request.url, request.redirected);

		} catch(e) {
			console.log("FETCH_ERROR", e?.message || e);
			throw e;
		}
	}

	private fetchRequest(url: string): Promise<Response> {
		if (this.payload) {
			return fetch(url, {
				method: "POST",
				body: this.payload,
				headers: this.getCsrfToken(),
			});
		}

		return fetch(url, { method: "GET" });
	}

	getCsrfToken() {
		const el = document.querySelector('meta[name="csrf-token"]');
		return el ? { "X-CSRF-Token": el.getAttribute("content")! } : {};
	}

	closeModal() {
		if (this.hasProgressOutlet) {
			window.dispatchEvent(new CustomEvent("open-modal"));
			this.progressOutlet?.updateText("Saving File");
		} else {
			console.log("Saving File");
		}

		setTimeout(() => {
			window.dispatchEvent(new CustomEvent("close-modal"));
			this.started = false;
		}, 2000);
	}

	async download(decrypt: boolean, type: string) {
		if (this.started) {
			return;
		}

		if (decrypt && !window.vault.is_open()) {
			window.vault.openModal(() => {
				this.download(decrypt, type);
			});
			return;
		}

		this.started = true;

		if (this.hasProgressOutlet) {
			window.dispatchEvent(new CustomEvent("open-modal"));
			this.progressOutlet?.updateText("Downloading Data");
		} else {
			console.log("Downloading Data");
		}

		let editRow: (fields: string[]) => Promise<string[]>;

		if (decrypt) {
			editRow = this.decryptRow.bind(this);
		} else {
			editRow = this.leaveRow;
		}

		if(this.hasPayloadCallbackValue) {
			this.payload = window[this.payloadCallbackValue]();
		}

		await this.downloadFileEditRows(this.urlValue, this.filenameValue, editRow);
	}
}
